import React, { useCallback, useLayoutEffect, useEffect, useMemo } from 'react';
import { View, Alert } from 'react-native';
import { format } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import Animated from 'react-native-reanimated';
import { IconPhone } from 'design-system/icons';
import Button from 'design-system/Button';
import { SettingsSection } from 'design-system/SettingsSection';
import { SettingsItemContent, SettingsItemDescription, SettingsItemNavigate, SettingsItemTitle, } from 'design-system/SettingsItem';
import VerificationStatusView from 'design-system/VerificationStatusView';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import withAnimatedNavigationHeader from 'design-system/hoc/withAnimatedNavigationHeader';
import { SCROLL_INDICATOR_INSETS } from 'design-system/values';
import { formatPhoneNumber } from 'utils/formatting';
import useColors from 'hooks/useColors';
import { DOB_DISPLAY_FORMAT } from 'hooks/useDateTimePickerDOB';
import { OnboardingFlow } from 'utils/types/navigation';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { selectUser } from 'reducers/selectors';
import { getUser } from 'actions';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import { HELP_ARTICLES_KEY } from 'features/help/constants';
import { useCommonInvestStyles } from './styles';
import { useAmplitudeOnMountEvent } from '../utils/analytics';
const strings = {
    alertTitle: 'Hold on 💫',
    alertMissingPhone: 'You need to specify your phone number.',
    phone: 'Phone number',
    verified: 'Verified',
    addPhoneNumber: 'Add...',
    buttonNext: 'Continue',
};
const ContactDetailsScreen = withAnimatedNavigationHeader(({ route, navigation, scrollHandler }) => {
    const flow = route.params?.flow;
    const commonStyles = useCommonInvestStyles();
    const user = useAppSelector(selectUser);
    const dispatch = useAppDispatch();
    useEffect(() => {
        dispatch(getUser());
    }, []);
    const colors = useColors();
    useLayoutEffect(() => {
        navigation.setOptions({
            headerRight: () => (<HelpScreenButton articlesKey={flow === OnboardingFlow.Pots ? HELP_ARTICLES_KEY.POTS_ONBOARDING : undefined}/>),
        });
    }, [colors.text.primary, flow]);
    const phoneNumber = user?.phoneNumber ? formatPhoneNumber(user.phoneNumber) : '';
    useAmplitudeOnMountEvent(flow, 'User Contact Screen');
    const changePhoneNumber = useCallback(() => {
        navigation.navigateDeprecated('PhoneVerification', {
            flow: 'invest',
            tintColor: colors.text.brand,
            onSuccess: () => navigation.pop(2),
        });
    }, [colors.text.brand]);
    const onSubmit = useCallback(() => {
        if (!phoneNumber) {
            Alert.alert(strings.alertTitle, strings.alertMissingPhone);
            return false;
        }
        if (!user.dateOfBirth) {
            return false;
        }
        const postData = { ...route.params.postData };
        navigation.navigateDeprecated('InvestCitizenshipDetails', {
            flow,
            title: route.params.title,
            firstName: route.params.firstName,
            middleName: route.params.middleName,
            lastName: route.params.lastName,
            dob: format(toZonedTime(user.dateOfBirth, 'UTC'), DOB_DISPLAY_FORMAT),
            postData,
        });
        return true;
    }, [
        phoneNumber,
        user.dateOfBirth,
        route.params.postData,
        route.params.title,
        route.params.firstName,
        route.params.middleName,
        route.params.lastName,
        flow,
    ]);
    const isButtonDisabled = useMemo(() => !phoneNumber || !user?.dateOfBirth, [phoneNumber, user?.dateOfBirth]);
    return (<View style={commonStyles.containerNoPadding}>
        <Animated.ScrollView onScroll={scrollHandler} scrollEventThrottle={16} keyboardShouldPersistTaps="handled" scrollIndicatorInsets={SCROLL_INDICATOR_INSETS} contentContainerStyle={commonStyles.contentContainer}>
          <ScreenInfoHeader title="Contact details"/>
          <SettingsSection noMarginTop>
            <SettingsItemNavigate icon={<IconPhone gradientKey="time"/>} onPress={changePhoneNumber}>
              <SettingsItemContent>
                <SettingsItemDescription>{strings.phone}</SettingsItemDescription>
                <SettingsItemTitle {...(!phoneNumber && {
        color: colors.text.placeholder,
    })}>
                  {phoneNumber || strings.addPhoneNumber}
                </SettingsItemTitle>
                {!!phoneNumber && <VerificationStatusView state="verified" label={strings.verified}/>}
              </SettingsItemContent>
            </SettingsItemNavigate>
          </SettingsSection>
        </Animated.ScrollView>
        <Button brand onPress={onSubmit} style={commonStyles.button} title={strings.buttonNext} disabled={isButtonDisabled}/>
      </View>);
}, ({ colors }) => ({
    headerColorOutputRange: [colors.background.dark, colors.cards.onDark],
}));
export default ContactDetailsScreen;
